import type {
  DTOSubscriptionBenefitGuide,
  DTOSubscriptionPageResponse,
} from 'src/apis/dto/DTOSubscriptionPage';

export interface PlanColumn {
  id: string;
  label: string;
  isHighPlan: boolean;
}

export interface BenefitRow {
  id: string;
  title: string;
  description?: string | null;
  iconSrc?: string;
  includedByPlan: Record<string, boolean>;
  guide?: DTOSubscriptionBenefitGuide;
}

export const buildBenefitTableData = (
  data: DTOSubscriptionPageResponse
): { planColumns: PlanColumn[]; benefitRows: BenefitRow[] } => {
  const { benefits, products, pages } = data;

  const upgradableToIds = new Set(
    pages.map((p) => p.upgradeAnchorPageId).filter((id): id is string => !!id)
  );

  const sortedPages = [...pages].sort((a, b) => {
    const aHigh = upgradableToIds.has(a.pageId) ? 1 : 0;
    const bHigh = upgradableToIds.has(b.pageId) ? 1 : 0;
    return aHigh - bHigh;
  });

  const planColumns: PlanColumn[] = sortedPages.map((p) => ({
    id: p.pageId,
    label: p.indicatorName,
    isHighPlan: upgradableToIds.has(p.pageId),
  }));

  const benefitRows: BenefitRow[] = benefits.map((benefit) => ({
    id: benefit.benefitId,
    title: benefit.name,
    description: benefit.description,
    iconSrc: benefit.imageUrl,
    includedByPlan: Object.fromEntries(
      planColumns.map((col) => [
        col.id,
        products
          .find((p) => p.productType === col.id)
          ?.benefits.some((b) => b.benefitId === benefit.benefitId) ?? false,
      ])
    ),
    guide: benefit.guide ?? undefined,
  }));

  return { planColumns, benefitRows };
};
